import {
  DeleteOutlined,
  ImageOutlined,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  InputBase,
  useTheme,
  Button,
  IconButton
} from "@mui/material";
import FlexBetween from "components/FlexBetween";
import Dropzone from "react-dropzone";
import UserImage from "components/UserImage";
import WidgetWrapper from "components/WidgetWrapper";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPosts } from "state";
import { BASE_URL } from "../../config";
import uploadToCloudinary from "../../uploadToCloudinary";

const MyPostWidget = ({ picturePath }) => {
  const dispatch = useDispatch();
  const [isImage, setIsImage] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [post, setPost] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const { palette } = useTheme();
  const { _id } = useSelector((state) => state.user);
  const token = useSelector((state) => state.token);
  const medium = palette.neutral.medium;

  const handlePost = async () => {
    try {
      setIsUploading(true);
      setUploadError("");

      let picturePath = "";
      if (image) {
        picturePath = await uploadToCloudinary(image);
      }

      const response = await fetch(`${BASE_URL}/posts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          userId: _id,
          description: post,
          picturePath: picturePath,
        }),
      });
      const posts = await response.json();

      dispatch(setPosts({ posts }));
      setImage(null);
      setImagePreview(null);
      setPost("");
      setIsUploading(false);
      setIsImage(false);
    } catch (error) {
      setIsUploading(false);
      setUploadError(error.message || "Failed to create post. Please try again.");
      console.error("Post creation error:", error);
    }
  };

  return (
    <WidgetWrapper mb="1rem">
      {/* Main Input Area */}
      <FlexBetween gap="1rem" mb="1rem">
        <UserImage image={picturePath} size="45px" />
        <InputBase
          placeholder="What's on your mind..."
          onChange={(e) => setPost(e.target.value)}
          value={post}
          multiline
          sx={{
            width: "100%",
            backgroundColor: palette.neutral.light,
            borderRadius: "1.5rem",
            padding: "0.75rem 1.25rem",
            fontSize: "0.95rem",
            "&:focus-within": {
              backgroundColor: palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.03)",
            }
          }}
        />
      </FlexBetween>

      {/* Image Upload Area */}
      {isImage && (
        <Box
          border={`1px solid ${medium}`}
          borderRadius="12px"
          p="1rem"
          mb="1rem"
          backgroundColor={palette.neutral.light}
        >
          <Dropzone
            acceptedFiles=".jpg,.jpeg,.png"
            multiple={false}
            onDrop={(acceptedFiles) => {
              const file = acceptedFiles[0];
              setImage(file);
              // Create preview URL
              const reader = new FileReader();
              reader.onloadend = () => {
                setImagePreview(reader.result);
              };
              reader.readAsDataURL(file);
            }}
          >
            {({ getRootProps, getInputProps }) => (
              <FlexBetween>
                <Box
                  {...getRootProps()}
                  border={`2px dashed ${palette.primary.main}`}
                  borderRadius="8px"
                  p="1rem"
                  width="100%"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                      borderColor: palette.primary.light,
                      backgroundColor: "rgba(0, 191, 255, 0.05)",
                    }
                  }}
                >
                  <input {...getInputProps()} />
                  {!image ? (
                    <Typography variant="body2" color={medium} textAlign="center">
                      Click or drag to add image
                    </Typography>
                  ) : (
                    <Box display="flex" alignItems="center" gap="1rem">
                      {imagePreview && (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            borderRadius: "8px"
                          }}
                        />
                      )}
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="500">
                          {image.name}
                        </Typography>
                        <Typography variant="caption" color={medium}>
                          Click to change
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                {image && (
                  <IconButton
                    onClick={() => {
                      setImage(null);
                      setImagePreview(null);
                    }}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                )}
              </FlexBetween>
            )}
          </Dropzone>
        </Box>
      )}

      {/* Error Message */}
      {uploadError && (
        <Typography
          color="error"
          sx={{
            mb: "1rem",
            p: "0.75rem",
            backgroundColor: "rgba(211, 47, 47, 0.08)",
            borderRadius: "8px",
            fontSize: "0.85rem",
            border: `1px solid rgba(211, 47, 47, 0.2)`,
          }}
        >
          {uploadError}
        </Typography>
      )}

      {/* Action Buttons */}
      <FlexBetween alignItems="center">
        <Button
          startIcon={<ImageOutlined />}
          onClick={() => setIsImage(!isImage)}
          size="small"
          sx={{
            color: palette.primary.main,
            textTransform: "none",
            fontSize: "0.9rem",
            fontWeight: 500,
            px: 1,
            "&:hover": {
              backgroundColor: "rgba(0, 191, 255, 0.08)",
            },
          }}
        >
          {isImage ? "Cancel" : "Add Image"}
        </Button>

        <Button
          disabled={!post || isUploading}
          onClick={handlePost}
          size="small"
          sx={{
            color: palette.background.alt,
            backgroundColor: palette.primary.main,
            borderRadius: "1.5rem",
            textTransform: "none",
            fontSize: "0.9rem",
            fontWeight: 600,
            px: 2.5,
            py: 0.6,
            boxShadow: "0 2px 8px rgba(0, 191, 255, 0.3)",
            "&:hover": {
              backgroundColor: palette.primary.light,
              boxShadow: "0 4px 12px rgba(0, 191, 255, 0.4)",
            },
            "&:disabled": {
              backgroundColor: palette.neutral.light,
              color: palette.neutral.main,
              boxShadow: "none",
            },
          }}
        >
          {isUploading ? "Posting..." : "Post"}
        </Button>
      </FlexBetween>
    </WidgetWrapper>
  );
};

export default MyPostWidget;